/**
 * Expandable epic with nested stories - supports drag-drop
 * Story 198: View and filter stories in backlog
 *
 * Uses @atlaskit/pragmatic-drag-and-drop for cross-epic story movement.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  attachClosestEdge,
  extractClosestEdge,
  type Edge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { StoryNode, isStoryDragData } from './StoryNode';
import { InlineEditableText } from './InlineEditableText';
import { useUpdateEpic, useDeleteEpic } from '../../hooks/useEpicMutations';
import type { StoryListItem } from '../../hooks/useStories';
import type { EpicListItem } from '../../hooks/useEpics';
import './EpicNode.css';

/** Epic drag data shape */
export interface EpicDragData {
  type: 'epic';
  epicSlug: string;
  [key: string]: unknown;
}

export function isEpicDragData(data: Record<string, unknown>): data is EpicDragData {
  return data.type === 'epic' && typeof data.epicSlug === 'string';
}

interface EpicNodeProps {
  epic: EpicListItem;
  stories: StoryListItem[];
  projectSlug: string;
  selectedStoryId: string | null;
  onSelectStory: (storyExternalId: string) => void;
  isExpanded: boolean;
  onToggleExpanded: () => void;
}

export function EpicNode({
  epic,
  stories,
  projectSlug,
  selectedStoryId,
  onSelectStory,
  isExpanded,
  onToggleExpanded,
}: EpicNodeProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [closestEdge, setClosestEdge] = useState<Edge | null>(null);
  const updateEpic = useUpdateEpic();
  const deleteEpic = useDeleteEpic();
  const dropRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  const handleTitleSave = useCallback(
    (newTitle: string) => {
      updateEpic.mutate({
        projectSlug,
        epicSlug: epic.slug,
        title: newTitle,
      });
    },
    [projectSlug, epic.slug, updateEpic]
  );

  const handleDelete = useCallback(() => {
    const storyCount = stories.length;
    const message =
      storyCount > 0
        ? `Delete epic "${epic.title}"?\n\n${storyCount} ${storyCount === 1 ? 'story' : 'stories'} will be moved to Unassigned.`
        : `Delete epic "${epic.title}"?`;

    if (window.confirm(message)) {
      deleteEpic.mutate({ projectSlug, epicSlug: epic.slug });
    }
  }, [epic.title, epic.slug, stories.length, projectSlug, deleteEpic]);

  // Make epic header draggable and a drop target for other epics
  useEffect(() => {
    const element = headerRef.current;
    if (!element) return;

    return combine(
      draggable({
        element,
        getInitialData: (): EpicDragData => ({
          type: 'epic',
          epicSlug: epic.slug,
        }),
        onDragStart: () => setIsDragging(true),
        onDrop: () => setIsDragging(false),
      }),
      dropTargetForElements({
        element,
        getData: ({ input, element: el }) => {
          return attachClosestEdge(
            { type: 'epic', epicSlug: epic.slug },
            { input, element: el, allowedEdges: ['top', 'bottom'] }
          );
        },
        canDrop: ({ source }) => {
          const data = source.data;
          // Only accept epic drops, not stories
          return isEpicDragData(data) && data.epicSlug !== epic.slug;
        },
        onDragEnter: ({ self }) => {
          setClosestEdge(extractClosestEdge(self.data));
        },
        onDrag: ({ self }) => {
          setClosestEdge(extractClosestEdge(self.data));
        },
        onDragLeave: () => {
          setClosestEdge(null);
        },
        onDrop: () => {
          setClosestEdge(null);
        },
      })
    );
  }, [epic.slug]);

  // Make epic content a drop target for stories
  useEffect(() => {
    const element = dropRef.current;
    if (!element) return;

    return dropTargetForElements({
      element,
      getData: () => ({ type: 'epic', epicSlug: epic.slug }),
      canDrop: ({ source }) => isStoryDragData(source.data),
      onDragEnter: () => setIsDragOver(true),
      onDragLeave: () => setIsDragOver(false),
      onDrop: () => setIsDragOver(false),
    });
  }, [epic.slug]);

  return (
    <div
      className={`epic-node ${isDragging ? 'epic-node--dragging' : ''}`}
      style={{ viewTransitionName: `epic-${epic.slug}` } as React.CSSProperties}
    >
      {closestEdge === 'top' && <div className="epic-node__drop-indicator epic-node__drop-indicator--top" />}
      <div ref={headerRef} className="epic-node__header">
        <div className="epic-node__drag-handle" aria-label="Drag to reorder">
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path d="M10 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm-4 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm1-5a1 1 0 1 0-2 0 1 1 0 0 0 2 0zm3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm1-5a1 1 0 1 0-2 0 1 1 0 0 0 2 0zM7 5a1 1 0 1 0-2 0 1 1 0 0 0 2 0z" />
          </svg>
        </div>
        <button
          className="epic-node__toggle"
          onClick={onToggleExpanded}
          type="button"
        >
          <svg
            className={`epic-node__chevron ${isExpanded ? 'epic-node__chevron--expanded' : ''}`}
            viewBox="0 0 16 16"
            fill="currentColor"
          >
            <path d="M6.22 3.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L9.94 8 6.22 4.28a.75.75 0 0 1 0-1.06z" />
          </svg>
        </button>
        <InlineEditableText
          value={epic.title}
          onSave={handleTitleSave}
          className="epic-node__title"
        />
        <button
          className="epic-node__delete"
          onClick={handleDelete}
          type="button"
          aria-label="Delete epic"
        >
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M6.5 1.75a.25.25 0 01.25-.25h2.5a.25.25 0 01.25.25V3h-3V1.75zm4.5 0V3h2.25a.75.75 0 010 1.5H2.75a.75.75 0 010-1.5H5V1.75C5 .784 5.784 0 6.75 0h2.5C10.216 0 11 .784 11 1.75zM4.496 6.675a.75.75 0 10-1.492.15l.66 6.6A1.75 1.75 0 005.405 15h5.19c.9 0 1.652-.681 1.741-1.576l.66-6.6a.75.75 0 00-1.492-.149l-.66 6.6a.25.25 0 01-.249.225h-5.19a.25.25 0 01-.249-.225l-.66-6.6z"
            />
          </svg>
        </button>
      </div>
      {closestEdge === 'bottom' && <div className="epic-node__drop-indicator epic-node__drop-indicator--bottom" />}

      {isExpanded && (
        <div
          ref={dropRef}
          className={`epic-node__stories ${isDragOver ? 'epic-node__stories--drag-over' : ''}`}
        >
          {stories.length === 0 ? (
            // Story 233: Empty state message for epics
            <div className="epic-node__empty">
              To add stories drag them here
            </div>
          ) : (
            stories.map((story) => (
              <StoryNode
                key={story.id}
                story={story}
                projectSlug={projectSlug}
                isSelected={story.external_id === selectedStoryId}
                onSelect={() => onSelectStory(story.external_id)}
                draggable
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}
